import {
  Bell,
  Building2,
  CalendarDays,
  Clock,
  Coffee,
  CreditCard,
  Globe,
  MapPin,
  MessageSquare,
  MonitorDot,
  Palette,
  Sliders,
  Tags,
  Umbrella,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

/** Stable identifier for a single organization settings pane. */
export type SectionId =
  | 'identity'
  | 'address'
  | 'branding'
  | 'domain'
  | 'general'
  | 'attendance'
  | 'break'
  | 'agent'
  | 'categories'
  | 'shift-alerts'
  | 'payroll'
  | 'leave'
  | 'notifications'
  | 'chat'

export type SectionGroupId = 'workspace' | 'operations' | 'people'

export interface SectionGroup {
  id: SectionGroupId
  label: string
}

export interface SectionMeta {
  id: SectionId
  group: SectionGroupId
  label: string
  /** Sentence shown under the pane heading. */
  description: string
  icon: LucideIcon
  /** Form paths owned by this pane — drives dirty dots and error routing. */
  fields: string[]
  /** Extra search terms so the rail filter matches intent, not just labels. */
  keywords: string
}

export const SECTION_GROUPS: readonly SectionGroup[] = [
  { id: 'workspace', label: 'Workspace' },
  { id: 'operations', label: 'Operations' },
  { id: 'people', label: 'People' },
] as const

export const SECTIONS: readonly SectionMeta[] = [
  {
    id: 'identity',
    group: 'workspace',
    label: 'Identity',
    description: 'Public profile, contact routes, and registration details.',
    icon: Building2,
    fields: [
      'name',
      'slug',
      'website',
      'contactEmail',
      'contactPhone',
      'registrationNumber',
      'taxId',
      'industry',
      'status',
    ],
    keywords: 'identity name slug website contact email phone registration tax industry status legal',
  },
  {
    id: 'address',
    group: 'workspace',
    label: 'Address',
    description: 'Registered business location used across records and correspondence.',
    icon: MapPin,
    fields: ['street', 'city', 'state', 'country', 'postalCode'],
    keywords: 'address street city state country postal code zip location registered',
  },
  {
    id: 'branding',
    group: 'workspace',
    label: 'Branding',
    description: 'Logo, accent color, footer text, and legal links.',
    icon: Palette,
    fields: ['primaryColor', 'footerText', 'termsUrl', 'privacyUrl'],
    keywords: 'branding brand logo color primary accent hex theme palette swatch footer legal terms privacy',
  },
  {
    id: 'domain',
    group: 'workspace',
    label: 'Custom domain',
    description: 'Serve your workspace from your own domain.',
    icon: Globe,
    // Saved through its own endpoints, not the settings form.
    fields: [],
    keywords: 'custom domain dns cname url address website subdomain white label',
  },
  {
    id: 'general',
    group: 'operations',
    label: 'General',
    description: 'Currency, timezone, date format, and the working week.',
    icon: Sliders,
    fields: [
      'defaultCurrency',
      'supportedCurrencies',
      'timezone',
      'dateFormat',
      'companyCodePrefix',
      'workWeek',
    ],
    keywords:
      'general default currency supported currencies timezone date format company code prefix work week days',
  },
  {
    id: 'attendance',
    group: 'operations',
    label: 'Attendance',
    description: 'What counts as present, how much lateness is forgiven, and pay deductions.',
    icon: Clock,
    fields: ['attendanceSettings'],
    keywords:
      'attendance minimum hours present late grace period auto deduct late minutes pay present',
  },
  {
    id: 'break',
    group: 'operations',
    label: 'Breaks',
    description: 'Default paid and unpaid break allowances, and excess break handling.',
    icon: Coffee,
    fields: ['breakSettings'],
    keywords: 'break breaks paid unpaid minutes auto deduct excess rest lunch',
  },
  {
    id: 'agent',
    group: 'operations',
    label: 'Desktop agent',
    description: 'Screenshot capture and inactivity monitoring for the desktop agent.',
    icon: MonitorDot,
    fields: ['agentSettings'],
    keywords:
      'agent desktop screenshot capture interval mode random fixed inactivity threshold alert monitoring',
  },
  {
    id: 'categories',
    group: 'operations',
    label: 'Categories',
    description: 'Organization-wide document and expense categories.',
    icon: Tags,
    fields: [],
    keywords: 'categories documents expenses company staff rename delete custom taxonomy',
  },
  {
    id: 'shift-alerts',
    group: 'people',
    label: 'Shift alerts',
    description: 'Pre-shift reminders and escalation when staff miss a shift.',
    icon: Bell,
    fields: ['shiftAlertSettings'],
    keywords:
      'shift alerts pre-shift reminder interval max missed escalate admin suppress leave approved',
  },
  {
    id: 'payroll',
    group: 'people',
    label: 'Payroll',
    description: 'Pay cycle cadence and the approval rules that lock a run.',
    icon: CreditCard,
    fields: ['payrollSettings'],
    keywords: 'payroll pay cycle monthly biweekly weekly approval finalize lock salary',
  },
  {
    id: 'leave',
    group: 'people',
    label: 'Leave',
    description: 'Approval workflow and balance rules for time off.',
    icon: Umbrella,
    fields: ['leaveSettings'],
    keywords: 'leave time off hr approval admin final negative balance holiday vacation',
  },
  {
    id: 'chat',
    group: 'people',
    label: 'Chat & channels',
    description:
      'Who can start direct conversations, create company channels, and send voice notes.',
    icon: MessageSquare,
    fields: ['chatSettings'],
    keywords:
      'chat channels direct messages dm voice notes staff management scope conversations messaging',
  },
  {
    id: 'notifications',
    group: 'people',
    label: 'Notifications',
    description: 'Channels used to deliver system notifications.',
    icon: CalendarDays,
    fields: ['notificationSettings'],
    keywords: 'notifications email in-app channels alerts delivery',
  },
] as const

export const DEFAULT_SECTION_ID: SectionId = 'identity'

/**
 * The sections this workspace can open. "Custom domain" is hidden while the
 * platform hasn't enabled custom domains (the API reports no target), so the
 * settings page doesn't advertise a feature that can't be used yet.
 */
export function availableSections(options: { customDomains: boolean }): readonly SectionMeta[] {
  return SECTIONS.filter((section) => section.id !== 'domain' || options.customDomains)
}

/**
 * The section to show. A selection that isn't available (a link to a hidden
 * section) opens the default section; a search that hides the selection
 * falls through to its first match.
 */
export function resolveActiveSection(
  selected: SectionId,
  available: readonly SectionMeta[],
  matches: readonly SectionMeta[]
): SectionId {
  const base = available.some((section) => section.id === selected) ? selected : DEFAULT_SECTION_ID
  if (matches.length === 0 || matches.some((section) => section.id === base)) return base
  return matches[0].id
}

export function isSectionId(value: string): value is SectionId {
  return SECTIONS.some((section) => section.id === value)
}

/** Ordered field paths used to jump to the first invalid control on submit. */
export const ERROR_FIELD_ORDER: readonly string[] = SECTIONS.flatMap((section) => section.fields)
